import { countryCodes } from "app/utils/CountryCodes";
import React, { useState, useRef, useEffect } from "react";
import styles from "./styles.module.scss";
import { cls } from "app/utils";

interface CountrySelectorProps {
  selected?: string;
  onSelect: (code: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  id?: string;
  left?: number;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
  selected,
  onSelect,
  placeholder = "Select Country",
  disabled = false,
  error = false,
  className,
  id,
  left = 20,
}) => {
  const [selectedCountry, setSelectedCountry] = useState(
    countryCodes.find((country) => country.code === selected) || null
  );
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (selected) {
      const country = countryCodes.find((c) => c.code === selected);
      setSelectedCountry(country || null);
    }
  }, [selected]);

  const filteredCountries = countryCodes.filter((country) =>
    country.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        if (selectedCountry) {
          setSearch("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedCountry]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setIsOpen(true);
    setHighlightedIndex(0);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setSearch("");
    setIsOpen(false);
    onSelect(country.code);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, filteredCountries.length - 1)
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === "Enter" && filteredCountries[highlightedIndex]) {
      event.preventDefault();
      handleCountrySelect(filteredCountries[highlightedIndex]);
    } else if (event.key === "Escape") {
      setIsOpen(false);
      if (selectedCountry) {
        setSearch("");
      }
    } else if (event.key.length === 1) {
      const firstMatchingCountry = filteredCountries.find(
        (country) => country.name[0].toLowerCase() === event.key.toLowerCase()
      );
      if (firstMatchingCountry) {
        const index = filteredCountries.indexOf(firstMatchingCountry);
        setHighlightedIndex(index);
      }
    }
  };

  return (
    <div
      className={cls(styles.country_selector_container, className, {
        [styles.error]: error,
      })}
    >
      <div className={styles.input_wrapper}>
        {!isOpen && selectedCountry && (
          <span className={styles.selected_flag} style={{ left: `${left}px` }}>
            {selectedCountry.flag}
          </span>
        )}
        <input
          ref={inputRef}
          id={id ?? "country-selector"}
          type="text"
          value={
            isOpen ? search : selectedCountry ? selectedCountry.name : search
          }
          onChange={handleSearchChange}
          onClick={() => {
            if (!disabled) {
              setIsOpen(true);
              setSearch("");
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cls(styles.country_input, {
            [styles.has_flag]: selectedCountry?.flag && !isOpen,
          })}
          style={{
            paddingLeft: (() => {
              if (selectedCountry?.flag && !isOpen) {
                return `${left + 25}px`;
              }
              if (!selectedCountry?.flag || isOpen) {
                return `${left}px`;
              }
              return undefined;
            })(),
          }}
        />
      </div>
      {isOpen && !disabled && (
        <div ref={dropdownRef} className={styles.country_dropdown}>
          {filteredCountries.length > 0 ? (
            <ul className={styles.country_list}>
              {filteredCountries.map((country, index) => (
                <li
                  key={country.code}
                  onClick={() => handleCountrySelect(country)}
                  className={`${styles.country_item} ${
                    index === highlightedIndex ? styles.highlighted : ""
                  } ${
                    selectedCountry?.code === country.code
                      ? styles.selected
                      : ""
                  }`}
                >
                  <span className={styles.country_flag}>{country.flag}</span>
                  <span className={styles.country_name}>{country.name}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.no_results}>No countries found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default CountrySelector;
